import { useEffect, useRef } from 'react'
import { secureLocalStorage } from '../../../utils/secureStorage'
import { checkAccountStatus, getFullData } from '../utils/accountBootstrap'
import { setLocalData } from '../utils/setLocalData'

const WEBSOCKET_URL = process.env.REACT_APP_WEBSOCKET_URL || 'wss://www.app.xmati.ai/apis/'
const baseUrl = window.location.origin
const ONLY_ADMIN = (baseUrl.includes('app.xmati.ai')) ? true : false

interface WebSocketHandlers {
  onBlockStatus: (status: string) => void
  onVersionUpdate: (serverVersion: string) => void
  onMaintenanceUpdate: (status: boolean) => void
  onDemoRestriction: (restrictionType: 'demo-only' | 'demo-not-allowed' | null) => void
  onLogout: () => void
  setIsLoading: (loading: boolean) => void
}

interface UseWebSocketConnectionParams {
  currentVersion: string
  workspaceRouteRegex: RegExp
  pathname: string
  activeTab: boolean
  handlers: WebSocketHandlers
}

/**
 * Custom hook to manage WebSocket connection for real-time communication
 * Handles registration, account validation, and message processing
 */
export const useWebSocketConnection = ({
  currentVersion,
  workspaceRouteRegex,
  pathname,
  activeTab,
  handlers
}: UseWebSocketConnectionParams) => {
  const didCheckAccountRef = useRef(false)
  const reconnectAttemptsRef = useRef(0)
  const MAX_RECONNECT_ATTEMPTS = 10
  const isLoggingOutRef = useRef(false)

  useEffect(() => {
    const userData = JSON.parse(secureLocalStorage.getItem('userData') || '{}')

    if (!workspaceRouteRegex.test(pathname)) {
      return // Only connect if on workspace/studio route
    }

    // Check if tab is active
    if (!activeTab) {
      return // Only connect if this is the active tab
    }

    let socket: WebSocket | null = null
    let reconnectTimeout: NodeJS.Timeout | null = null
    let isMounted = true

    const connectWebSocket = () => {
      if (!isMounted) {
        return
      }

      socket = new WebSocket(WEBSOCKET_URL)

      socket.onopen = () => {
        // Reset reconnect attempts on successful connection
        reconnectAttemptsRef.current = 0

        // Unblock on successful connection
        handlers.onMaintenanceUpdate(false)
        const userId = userData.email

        socket?.send(JSON.stringify({
          type: 'REGISTER_CHILD',
          userId,
          clientVersion: currentVersion,
        }))

        if (!didCheckAccountRef.current) {
          didCheckAccountRef.current = true

          void (async () => {
            handlers.setIsLoading(true)

            try {
              if (!userData.email) {
                handlers.setIsLoading(false)
                alert('Session is invalid. Please log in again.')
                handlers.onLogout()
                return
              }

              // Execute both operations in parallel
              const [accountStatusRes, fullDataRes] = await Promise.all([
                checkAccountStatus(userData.email),
                getFullData(userData.email)
              ])

              // Handle account status response
              if (!accountStatusRes.ok) {
                handlers.setIsLoading(false)
                alert('There was a problem validating your account. Please log in again.')
                handlers.onLogout()
                return
              }

              // Handle full data response
              if (fullDataRes.ok) {
                const { formData, subData } = await fullDataRes.json()
                if (formData && subData) {
                  // Check for demo account restrictions
                  if (ONLY_ADMIN && subData.subscription !== 'Demo' && (formData.email !== 'xmatiservice@gmail.com' && formData.email !== 'pentest1@pentesterhub.com' && formData.email !== 'pentest2@pentesterhub.com' && formData.email !== 'suyogamin11@gmail.com')) {
                    handlers.setIsLoading(false)
                    handlers.onDemoRestriction('demo-only')
                    return
                  }

                  if (!ONLY_ADMIN && subData.subscription === 'Demo') {
                    handlers.setIsLoading(false)
                    handlers.onDemoRestriction('demo-not-allowed')
                    return
                  }

                  // Clear any previous restrictions
                  handlers.onDemoRestriction(null)

                  setLocalData(formData, subData)
                } else {
                  handlers.setIsLoading(false)
                  alert('Could not retrieve full user data. Please log in again.')
                  handlers.onLogout()
                  return
                }
              } else {
                handlers.setIsLoading(false)
                const { msg } = await fullDataRes.json()
                alert(`There was a problem retrieving full user data. ${msg}. Please log in again.`)
                handlers.onLogout()
                return
              }

              // Both operations completed successfully - stop loader
              handlers.setIsLoading(false)

            } catch (err: any) {
              // Stop loader on error
              handlers.setIsLoading(false)

              console.error('[account-bootstrap] error:', err?.name, err?.message, err)
              if (err?.name === 'AbortError') {
                alert('Checking your account status timed out. Please log in again.')
              } else {
                alert('Unable to retrieve account data right now. Please log in again.')
              }
              handlers.onLogout()
            }
          })()
        }
      }

      socket.onmessage = (event) => {
        if (!isMounted) {
          return
        }
        const data = JSON.parse(event.data)

        switch (data.type) {
          case 'REGISTER_SUCCESS':
            break
          case 'DUPLICATE_SESSION':
            isLoggingOutRef.current = true
            alert('You or Someone else is already logged in from another tab or device. Please close the other session and try again.')
            handlers.onLogout()
            break
          case 'error':
            isLoggingOutRef.current = true
            alert(`WebSocket error: ${data.message || 'Unknown error'}`)
            handlers.onLogout()
            break
          case 'FORCE_LOGOUT':
            isLoggingOutRef.current = true
            if (data.alert) {
              alert(data.message || 'You have been logged out by the server.')
            }
            handlers.onLogout()
            break
          case 'BLOCK_STATUS':
            handlers.onBlockStatus(data.message)
            break
          case 'VERSION_UPDATE':
            handlers.onVersionUpdate(data.message)
            break
          case 'MAINTENANCE_STATUS':
            handlers.onMaintenanceUpdate(data.message)
            break
          default:
            isLoggingOutRef.current = true
            alert(`[WebSocket] Unknown message type: ${data.type}`)
            handlers.onLogout()
            break
        }
      }

      socket.onerror = (error) => {
        // Increment reconnect attempts on error
        reconnectAttemptsRef.current += 1

        // On error, optimistically block the user with maintenance screen
        handlers.onMaintenanceUpdate(true)
        didCheckAccountRef.current = false

        // Check if max reconnect attempts reached
        if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          isLoggingOutRef.current = true
          console.error('[WebSocket] Max reconnect attempts reached. Logging out.')
          alert('Unable to maintain connection to the server. Please log in again.')
          handlers.onLogout()
        }
      }

      socket.onclose = () => {
        if (!isMounted || isLoggingOutRef.current) {
          return
        }
        reconnectTimeout = setTimeout(connectWebSocket, 1000)
      }
    }

    connectWebSocket()

    return () => {
      isMounted = false
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout)
      }
      socket?.close()
    }
  }, [pathname, activeTab])
}
